import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, LineChart, Line, CartesianGrid, Cell,
} from "recharts";
import { fmtNum, fmtMoney } from "./format";

// Paleta categórica validada (modo oscuro) · orden fijo por período: Actual, -1 año, -2 años…
export const PERIOD_COLORS = ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181", "#008300"];
const TT = { background: "#101410", border: "1px solid #2a2a2a", fontSize: 11 };
const AX = { fill: "#888", fontSize: 10 };

function Box({ title, id, children }) {
  return (
    <div id={id} className="bg-app/40 border border-border/40 rounded-lg p-3" data-testid={id}>
      <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">{title}</div>
      {children}
    </div>
  );
}

/** Tendencia mensual: una línea por período, alineada por mes relativo al inicio del rango. */
export function TrendChart({ trend, periodos, metrica }) {
  const key = metrica === "monto" ? "monto" : "unidades";
  const rows = [];
  (trend || []).forEach((t, idx) => {
    const start = (t.periodo?.desde || "").slice(0, 7);
    const [sy, sm] = start.split("-").map(Number);
    (t.puntos || []).forEach((p) => {
      const [y, m] = p.mes.split("-").map(Number);
      const off = (y - sy) * 12 + (m - sm);
      if (!rows[off]) rows[off] = { idx: off };
      rows[off][`p${idx}`] = p[key];
      if (idx === 0) rows[off].mes = p.mes;
    });
  });
  const data = rows.filter(Boolean).map((r) => ({ ...r, mes: r.mes || `Mes ${r.idx + 1}` }));
  const fmt = metrica === "monto" ? (v) => fmtMoney(v) : fmtNum;
  return (
    <Box title={`Tendencia mensual · ${metrica === "monto" ? "Monto" : "Unidades"}`} id="chart-tendencia">
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
          <XAxis dataKey="mes" tick={AX} />
          <YAxis tick={AX} />
          <Tooltip contentStyle={TT} formatter={(v) => fmt(v)} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {periodos.map((p, i) => (
            <Line key={p.i} type="monotone" dataKey={`p${i}`} name={p.label} stroke={PERIOD_COLORS[i % 6]}
              strokeWidth={2} dot={{ r: 3 }} strokeDasharray={i === 0 ? undefined : "4 3"} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

/** Comparación entre años: total del indicador por período. */
export function YearsChart({ total, periodos, metrica }) {
  const key = metrica === "monto" ? "monto" : "unidades";
  const data = periodos.map((p, i) => ({ label: p.label, v: total?.[`${key}_${p.i}`] || 0, fill: PERIOD_COLORS[i % 6] }));
  const fmt = metrica === "monto" ? (v) => fmtMoney(v) : fmtNum;
  return (
    <Box title={`Comparación entre años · ${key === "monto" ? "Monto" : "Unidades"}`} id="chart-anios">
      {total?.unidades_mixtas && key === "unidades" && (
        <div className="text-[10px] text-amber-300 mb-1">Incluye unidades de medida distintas; ver totales por unidad en la tabla.</div>
      )}
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
          <XAxis dataKey="label" tick={AX} />
          <YAxis tick={AX} />
          <Tooltip contentStyle={TT} formatter={(v) => fmt(v)} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
          <Bar dataKey="v" name={key === "monto" ? "Monto" : "Unidades"} radius={[4, 4, 0, 0]} maxBarSize={48}>
            {data.map((d) => <Cell key={d.label} fill={d.fill} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

/** Ranking horizontal (clientes o productos): actual vs 1er comparativo. */
export function RankingChart({ title, id, items, metrica, periodos }) {
  const key = metrica === "monto" ? "monto" : "unidades";
  const data = (items || []).map((r) => ({
    grupo: String(r.grupo || "").slice(0, 26), a: r[`${key}_0`], b: r[`${key}_1`],
  }));
  const fmt = metrica === "monto" ? (v) => fmtMoney(v) : fmtNum;
  const hasComp = periodos.length > 1;
  return (
    <Box title={title} id={id}>
      {data.length === 0 ? <div className="p-6 text-center text-xs text-text-muted">Sin datos</div> : (
        <ResponsiveContainer width="100%" height={Math.max(180, data.length * (hasComp ? 30 : 22) + 40)}>
          <BarChart data={data} layout="vertical" margin={{ top: 5, right: 16, left: 8, bottom: 5 }} barGap={2}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" horizontal={false} />
            <XAxis type="number" tick={AX} />
            <YAxis type="category" dataKey="grupo" tick={AX} width={150} />
            <Tooltip contentStyle={TT} formatter={(v) => fmt(v)} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
            {hasComp && <Legend wrapperStyle={{ fontSize: 11 }} />}
            <Bar dataKey="a" name={periodos[0]?.label || "Actual"} fill={PERIOD_COLORS[0]} radius={[0, 4, 4, 0]} maxBarSize={12} />
            {hasComp && <Bar dataKey="b" name={periodos[1]?.label} fill={PERIOD_COLORS[1]} radius={[0, 4, 4, 0]} maxBarSize={12} />}
          </BarChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
}
